/**
 * `people` (§7.7) — Access list. Ported from `prototype/screens/people.js`. D89's `expired` row,
 * and the seen-clear this screen owns: it calls
 * `POST /gates/{id}/members/seen` when the manager opens the roster, and never computes the
 * count itself (P5's). Both copy directions are P15's and are not rendered here (out of scope).
 *
 * Roster credential editing (role change + revoke) is this screen's too — the server side has
 * been deployed since P5; only the client call was missing. A manager's row opens a bottom sheet
 * (`.member-sheet`, following `NudgeSheet`'s bottom-anchored, safe-area-owning pattern — D132) with
 * a role toggle and a destructive revoke behind its own confirm step. A resident sees the same
 * static rows as before: no sheet, no affordance.
 *
 * **One list, and the role is a glyph rather than a heading.** The roster used to be two sections
 * under two titles, which on the common gate drew a heading over a single manager and then pushed
 * the add/copy controls below both lists — off screen on a real roster. Managers still sort first,
 * and each carries a `user` glyph **after** the name, in the run the `installer` and `expired`
 * chips already occupy — a mark on the person rather than a bullet the list is indented from. The
 * two section titles are retired, the app bar
 * already naming the gate that `managersTitle` was interpolating. `managers` survives as a derived
 * array because `orphansTheGate()` counts it, and that count is D143's client half.
 */
import { useEffect, useState } from 'react'
import type { GateId } from '@selogate/core'
import { useSession } from '../../auth/context'
import { NetworkError } from '../../auth/transport'
import { useDeviceCache } from '../../device-cache/context'
import {
  AlertPopover,
  AppBar,
  Bidi,
  FrameOverlay,
  Icon,
  MemberPhone,
  PrimaryButton,
  SecondaryButton,
} from '../../components'
import type { Key } from '../../i18n/catalogs'
import { useTranslation } from '../../i18n/I18nProvider'
import { useDismissOnBack, useNavigation } from '../../router'
import {
  getRoster,
  markRosterSeen,
  patchInvitation,
  patchMember,
  revokeInvitation,
  revokeMember,
  type Roster,
  type RosterRow,
} from '../shared/api'
import { takeGateHandoff } from '../shared/gateHandoff'
import {
  TRANSPORT_ICONS,
  TRANSPORT_KEYS,
  TRANSPORT_ORDER,
  transportChoiceOf,
  transportPatchValue,
  type TransportChoice,
} from '../shared/transportChoice'

type MutationError = 'grant-not-editable' | 'generic'

/**
 * **The roster is three states, not one nullable value.**
 *
 * It used to be `Roster | undefined`, and both sections read `roster?.members.filter(…) ?? []` —
 * so an in-flight fetch and a gate with genuinely no members produced the identical render, and
 * `loadRoster` swallowing its failure with a bare `return` made the *third* case look like the
 * second **forever**. "No one has access to this gate" is a claim (entry 16's Home rule, one
 * screen over), and this screen was making it while it was still asking, and again after it had
 * been refused. On a gate with a full roster.
 */
type RosterState =
  | { readonly phase: 'loading' }
  | { readonly phase: 'ready'; readonly roster: Roster }
  | { readonly phase: 'failed' }
  /**
   * R1-7(c). A resident who deep-links here gets the server's honest `403 manager-only` — that
   * is not a disconnection and `retryLoad()` cannot fix it, so it is its own phase rather than a
   * `failed` the screen invites a retry on. Distinct from `failed` on purpose: a manager-only
   * refusal is knowable and permanent for this viewer, and no reload changes it.
   */
  | { readonly phase: 'forbidden' }

export function PeopleScreen({ gateId }: { readonly gateId: GateId }): JSX.Element {
  const session = useSession()
  const deviceCache = useDeviceCache()
  const navigate = useNavigation()
  const { t } = useTranslation('people')
  const { t: tCommon } = useTranslation('common')
  /**
   * **D269 — the transport control's three strings are the invite row's, read rather than copied.**
   *
   * The manager sets this restriction in two places and it must read the same in both; a `people`
   * copy of *"ענן בלבד"* is two strings in five catalogs that agree until one of them is
   * retranslated. Reading a second namespace is `GateDetailScreen`'s own shape (`gate` + `access` +
   * `ble` + `removeMe`), and D255's rule beneath it: a second surface for strings that already have
   * an owner is not a namespace of its own.
   */
  const { t: tInvite } = useTranslation('invite')

  const [rosterState, setRosterState] = useState<RosterState>({ phase: 'loading' })
  /**
   * **`origin` is "has the cache answered yet", and this screen's chrome depends on it.**
   *
   * `gateName` and `canManage` are both read off the cached gate, and the cache is empty until
   * the IndexedDB snapshot restores — a few hundred milliseconds after mount on a *cold* entry
   * (a deep link, a reload, an OS-discarded PWA relaunched at this URL). During that window
   * `cached` is `undefined`, so `canManage` was `false` and a **manager saw the resident view**:
   * no member sheet, no "add member" button, and a blank name in the title. Not a slow render —
   * a wrong one, of exactly the kind entry 16 removed from Home and entry 17 from the roster
   * itself. It is the same defect a third time, one field further out.
   *
   * `origin` is `undefined` before either a restore or a server fetch and set by both, so it is
   * the honest "we do not know yet" signal; `cached === undefined` *after* it is set means the
   * gate genuinely is not ours, which the roster read answers with a `403` and the `failed`
   * phase renders. Deliberately **not** gated on `cached` itself, which would hang forever on a
   * gate that is legitimately absent.
   */
  const cacheAnswered = deviceCache.origin !== undefined
  const cached = deviceCache.gates.find((g) => g.gateId === gateId)
  const gateName = cached?.alias ?? cached?.defaultName ?? ''
  const canManage = cacheAnswered && cached?.viewerRole === 'manager'

  const [selectedRow, setSelectedRow] = useState<RosterRow | undefined>(undefined)
  const [confirmingRevoke, setConfirmingRevoke] = useState(false)
  const [busy, setBusy] = useState(false)
  const [mutationError, setMutationError] = useState<MutationError | undefined>(undefined)
  /**
   * D143's `409`, surfaced as an alert rather than as a `MutationError`. It is deliberately not a
   * third member of that union: those two render inline under the sheet's controls as *"try
   * again"*-shaped copy, and this one is a refusal that no retry changes.
   */
  const [lastManagerAlert, setLastManagerAlert] = useState(false)

  /**
   * D145 — Android's hardware back closes the member sheet instead of leaving the screen.
   *
   * One entry for the sheet, not a second for `confirmingRevoke`: that is an inline expansion
   * rather than a layer, and back dismisses the whole sheet exactly as the backdrop does — this
   * is a mirror of `closeSheet`, which already resets the confirmation on the way out.
   *
   * Left dismissible while `busy`, again mirroring the backdrop, which this sheet does not gate.
   * The role and revoke writes both close the sheet themselves on success, so a back tap during
   * one races nothing the user can see: the request completes and `loadRoster()` still runs.
   */
  useDismissOnBack('people:member-sheet', selectedRow !== undefined, () => closeSheet())
  /**
   * D143's last-manager refusal, which is an `AlertPopover` — one dismiss action and no confirm.
   * Back is that dismiss action: an alert states a rule the user cannot argue with, so there is
   * no state to protect and nothing a back tap could strand. It is registered **after** the sheet,
   * so when both are up the first back clears the alert and the second closes the sheet.
   */
  useDismissOnBack('people:last-manager', lastManagerAlert, () => setLastManagerAlert(false))

  /**
   * **R5 step 5 — the Access page one screen up has usually just read exactly this.**
   *
   * `handed` is taken once, at mount, and only the *initial* load may use it: a reload behind a
   * mutation (`loadRoster` is called again after a role change or a revoke) must read the server,
   * or the list would be re-seeded with the roster as it was before the edit. That is what
   * `initial` distinguishes, and it is the whole of the risk in this handoff.
   *
   * A miss — a deep link, a second visit, a manager who tapped through before the deposit landed —
   * reads as it always did. `gateHandoff.ts`'s slots are read-once, so nothing here can serve a
   * stale roster twice.
   */
  async function loadRoster(initial = false): Promise<void> {
    // R1-6. This `await` used to have nothing catching it: a `NetworkError` left `rosterState`
    // at `loading` forever, so the screen's own `failed`+retry design was unreachable for the
    // commonest failure and `retryLoad` re-entered the same hole. Both the read below and the
    // seen-write/refresh that can follow it are covered by the one `try`.
    try {
      const handed = initial ? takeGateHandoff(gateId, 'roster') : undefined
      if (handed !== undefined) {
        setRosterState({ phase: 'ready', roster: handed })
        // D89's seen-write still runs on the handed-down roster: what clears the badge is the
        // manager **opening** the list, which is what has just happened — the count's provenance
        // has nothing to do with it.
        if (handed.unseenExpired > 0) {
          await markRosterSeen(session.request, gateId)
          await deviceCache.refresh()
        }
        return
      }
      const result = await getRoster(session.request, gateId)
      if (!result.ok) {
        // R1-7(c). A `403` is the server's honest "you may not see this roster" — distinct from
        // every other refusal, which stays `failed`+retry.
        if (result.status === 403) {
          setRosterState({ phase: 'forbidden' })
          return
        }
        // A reload behind a mutation keeps the roster already on screen — the edit succeeded, and
        // replacing a good list with an error because the *re-read* failed would be a worse lie
        // than a slightly stale one. Only a load with nothing to fall back on becomes `failed`.
        setRosterState((prev) => (prev.phase === 'ready' ? prev : { phase: 'failed' }))
        return
      }
      setRosterState({ phase: 'ready', roster: result.data })
      if (result.data.unseenExpired > 0) {
        // D89's badge is server-side state, and clearing it there is only half the job: Gate
        // Detail's People row and Home both read `unseenExpired` off the **cached gate**, which
        // nothing re-read — so the badge the manager had just answered kept showing until the next
        // launch. Awaited, then refreshed, in that order: a refresh racing the seen-write would
        // fetch the count it is meant to clear.
        await markRosterSeen(session.request, gateId)
        await deviceCache.refresh()
      }
    } catch (error) {
      if (!(error instanceof NetworkError)) throw error
      // Same rule as the failed-read branch above: a roster already on screen survives a failed
      // seen-write/refresh rather than being replaced by an error over a successful load.
      setRosterState((prev) => (prev.phase === 'ready' ? prev : { phase: 'failed' }))
    }
  }

  useEffect(() => {
    void loadRoster(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateId])

  function retryLoad(): void {
    setRosterState({ phase: 'loading' })
    void loadRoster()
  }

  const roster = rosterState.phase === 'ready' ? rosterState.roster : undefined
  const managers = roster?.members.filter((m) => m.role === 'manager') ?? []
  const residents = roster?.members.filter((m) => m.role === 'resident') ?? []
  /**
   * Managers first, then residents, each keeping the order the server sent. A single list still
   * has to answer *who runs this gate*, and sorting is what carries that now the headings do not —
   * an interleaved list would leave the glyph to be hunted for down a roster of forty.
   */
  const members = [...managers, ...residents]

  /**
   * What the screen draws. The roster and the cache are two independent arrivals and the screen
   * needs both — one for the rows, one for whether they are actionable — so the wait is the
   * longer of the two. `failed`/`forbidden` win over the wait: an offline cold launch with no
   * stored snapshot never sets `origin` at all, and holding a spinner forever in front of a
   * request that has already been refused is the same lie in a politer font.
   */
  const phase: 'loading' | 'failed' | 'forbidden' | 'ready' =
    rosterState.phase === 'failed' || rosterState.phase === 'forbidden'
      ? rosterState.phase
      : !cacheAnswered || rosterState.phase === 'loading'
        ? 'loading'
        : 'ready'

  /**
   * D143's invariant, seen from the client: the last manager may not be demoted.
   *
   * **The server now refuses this** — `gateLifecycleWrite()` throws on a `managerCount` decrement
   * to zero and the route answers `409 last-manager`. That is the authority; this is why the
   * toggle is withheld rather than offered and refused, which is the better shape when the answer
   * is knowable from data the screen already has. The `409` is still handled below, because the
   * roster this reads can be stale (a co-manager can leave between the read and the tap).
   *
   * Why the rule is worth holding at all: it is one-way. *Every* roster route is behind
   * `requireManager`, so a gate whose last manager became a resident has no route left that can
   * promote anyone back — it is unmanageable from the app, permanently.
   *
   * **Keyed on sole-manager, not on self.** With a second manager present, demoting yourself is an
   * ordinary handover and blocking it would be wrong; the count is what makes it unrecoverable.
   * `isSelf` is belt-and-braces here rather than load-bearing — reaching this sheet's controls
   * requires `canManage`, so a one-manager roster the viewer can act on is necessarily their own
   * row — and it stays because it costs nothing and states the intent.
   */
  function orphansTheGate(row: RosterRow): boolean {
    return isSelf(row) && row.role === 'manager' && managers.length <= 1
  }

  /**
   * **D236 — a member and an invitation are addressed differently now, so the screen decides
   * which from the ROW rather than from the shape of an identifier.**
   *
   * This used to be `row.userId ?? row.phone`, handed to one endpoint pair that told the two
   * apart by a leading `+`. That is what put a pending invitee's number into a URL — and the
   * roster has carried `kind` all along, so the discriminator was always available here without
   * inspecting the value.
   */
  type MutationTarget =
    { readonly kind: 'member'; readonly userId: string } | { readonly kind: 'invite'; readonly phone: string }

  function targetOf(row: RosterRow): MutationTarget | undefined {
    if (row.kind === 'member') {
      return row.userId === undefined ? undefined : { kind: 'member', userId: row.userId }
    }
    return row.phone === undefined ? undefined : { kind: 'invite', phone: row.phone }
  }

  /**
   * The signed-in manager's own membership row. "Remove me" belongs to Gate Detail's
   * `RemoveMePopover` (D144 deleted the `removeMe` route it used to name), not to this sheet.
   */
  function isSelf(row: RosterRow): boolean {
    return row.kind === 'member' && row.userId !== undefined && row.userId === session.userId
  }

  function openSheet(row: RosterRow): void {
    setSelectedRow(row)
    setConfirmingRevoke(false)
    setMutationError(undefined)
  }

  function closeSheet(): void {
    setSelectedRow(undefined)
    setConfirmingRevoke(false)
    setMutationError(undefined)
  }

  async function changeRole(row: RosterRow, role: 'manager' | 'resident'): Promise<void> {
    const target = targetOf(row)
    if (target === undefined || role === row.role || busy) return
    // The render withholds the toggle in this case; this is the same rule at the call site, so a
    // future affordance cannot reach the orphaning path by rendering its own button.
    if (role === 'resident' && orphansTheGate(row)) return
    setBusy(true)
    setMutationError(undefined)
    // R1-6. `busy` used to be reset right after the `await`, so a `NetworkError` here left it
    // `true` forever — the role chips and revoke stayed disabled, the sheet stuck open, with no
    // error shown. The reset now lives in `finally`, unconditionally, and a non-`NetworkError`
    // is rethrown after it rather than before it.
    try {
      const result =
        target.kind === 'member'
          ? await patchMember(session.request, gateId, target.userId, { role })
          : await patchInvitation(session.request, gateId, target.phone, { role })
      if (!result.ok) {
        // D143. The render withholds the toggle when the roster already shows a sole manager, so
        // reaching this means the roster was stale — a co-manager left in between. An alert rather
        // than the inline error, because the inline one reads as "try again" and this will not
        // succeed on a retry; `loadRoster()` re-reads so the withheld toggle then agrees with it.
        if (result.code === 'last-manager') {
          setLastManagerAlert(true)
          void loadRoster()
          return
        }
        setMutationError(result.code === 'grant-not-editable' ? 'grant-not-editable' : 'generic')
        return
      }
      closeSheet()
      /**
       * **The cached gate carries `viewerRole`, and a role change can be the viewer's own.**
       *
       * `loadRoster()` re-reads the roster, which is what this screen renders — but Home's manager
       * chip and Gate Detail's whole manager-only section read `viewerRole` off the *cached gate*,
       * which nothing here touched. Hand yourself over to a co-manager and the app kept offering
       * you manager surfaces until the next launch, where every one of them would have been
       * refused by the server. Same shape as the badge above, and as the install gap in entry 16:
       * a mutation whose effect the cache never heard about.
       *
       * Unconditional rather than `isSelf`-gated: an invitation has no `userId`, so "was that
       * me" is not always answerable here, and a refresh costs one request.
       */
      await Promise.all([loadRoster(), deviceCache.refresh()])
    } catch (error) {
      if (!(error instanceof NetworkError)) throw error
      setMutationError('generic')
    } finally {
      setBusy(false)
    }
  }

  /**
   * **D269 — the grant's transport restriction, set from the same sheet as the role.**
   *
   * P5 has served this since it shipped (`toGrantPatch` in `members/routes.ts` takes
   * `transportRestriction`, and `null` clears it), and `GET …/members` has always returned it; what
   * was missing was a control. The invite row got one with P16, so a manager could restrict a
   * *new* member and never change their mind — which is the asymmetry this closes.
   *
   * **Three things it deliberately does not do.**
   *
   * It does **not close the sheet**, unlike `changeRole`. A role change moves the row — sections,
   * glyph, the sole-manager rule — so leaving is the honest answer there; this one changes a field
   * the row does not draw, so the chip that lights up *is* the receipt, and closing would leave the
   * manager with no statement of what they had just chosen. A manager setting both in one visit
   * gets one sheet rather than two.
   *
   * It does **not re-read the roster**, and that is not laziness. `GET …/members` is an
   * eventually-consistent Query, so a reload racing this write can hand back the pre-write value —
   * on a control that stays open, that renders as the tap having been refused. The `2xx` is the
   * authority for the one field it just set, so the row is updated from what was **asked**: the
   * server echoes `role`, `status` and `memberLabel` and has never echoed this field, and adding an
   * echo would make an absent value mean *cleared* on a new bundle and *this stage predates the
   * field* on an old one — indistinguishable, and wrong in silence on a deploy skew.
   *
   * It does **not refuse the last manager.** D143 guards `managerCount`; a transport restriction
   * reaches `evaluateAccess()` and therefore only gate *commands* — every roster route stays open
   * to a manager whose own grant is BLE-only, so no one can lock themselves out of undoing this.
   */
  async function changeTransport(row: RosterRow, choice: TransportChoice): Promise<void> {
    const target = targetOf(row)
    if (target === undefined || busy || choice === transportChoiceOf(row.transportRestriction)) return
    setBusy(true)
    setMutationError(undefined)
    const body = { transportRestriction: transportPatchValue(choice) }
    // R1-6, and the `finally` for the same reason `changeRole` has one: a `NetworkError` here used
    // to be the shape that left every control in this sheet disabled with nothing said.
    try {
      const result =
        target.kind === 'member'
          ? await patchMember(session.request, gateId, target.userId, body)
          : await patchInvitation(session.request, gateId, target.phone, body)
      if (!result.ok) {
        // An `expired` grant answers `409 grant-not-editable` — the render withholds the control in
        // that case, so reaching this means the row was stale (it expired between the read and the
        // tap), and the copy names the rule rather than inviting a retry.
        setMutationError(result.code === 'grant-not-editable' ? 'grant-not-editable' : 'generic')
        return
      }
      // Destructured rather than spread-with-`undefined`: `'any'` is the *absence* of the field
      // (D46), and a row carrying `transportRestriction: undefined` is a different object from one
      // that does not carry it — the snapshot rules one directory over are written against exactly
      // that distinction.
      const { transportRestriction: _cleared, ...withoutRestriction } = row
      const updated: RosterRow =
        choice === 'any' ? withoutRestriction : { ...row, transportRestriction: choice }
      // Both, and the list one is not cosmetic: `openSheet` reads the *rendered* row, so a sheet
      // closed and reopened without it would show the value this tap replaced.
      setSelectedRow(updated)
      setRosterState((prev) =>
        prev.phase !== 'ready'
          ? prev
          : {
              phase: 'ready',
              roster: {
                ...prev.roster,
                members: prev.roster.members.map((m) => (rowKey(m) === rowKey(row) ? updated : m)),
              },
            },
      )
    } catch (error) {
      if (!(error instanceof NetworkError)) throw error
      setMutationError('generic')
    } finally {
      setBusy(false)
    }
  }

  async function revoke(row: RosterRow): Promise<void> {
    const target = targetOf(row)
    if (target === undefined || busy) return
    setBusy(true)
    setMutationError(undefined)
    // R1-6, same shape as `changeRole` above.
    try {
      const result =
        target.kind === 'member'
          ? await revokeMember(session.request, gateId, target.userId)
          : await revokeInvitation(session.request, gateId, target.phone)
      if (!result.ok) {
        // Same rule on the revoke path. The sheet hides revoke for the viewer's own row, so this is
        // the co-manager case: two managers on the roster, one of whom left before this landed.
        if (result.code === 'last-manager') {
          setLastManagerAlert(true)
          void loadRoster()
          return
        }
        setMutationError(result.code === 'grant-not-editable' ? 'grant-not-editable' : 'generic')
        return
      }
      closeSheet()
      void loadRoster()
    } catch (error) {
      if (!(error instanceof NetworkError)) throw error
      setMutationError('generic')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <AppBar title={t('title', { name: gateName })} onBack={() => navigate.back()} />
      {/*
        The three phases render three different things, and only the third one draws sections.
        Section titles above a `—` are the shape that made "still loading" and "nobody has
        access" indistinguishable, so neither of the other two phases gets to draw them.
      */}
      {phase === 'loading' ? (
        <div className="roster-notice">{tCommon('loading')}</div>
      ) : phase === 'forbidden' ? (
        // R1-7(c). No retry — a `403` is not a disconnection, and reloading cannot change who
        // the server thinks the viewer is.
        <div className="roster-notice">
          <p className="hint error">{t('managersOnly')}</p>
        </div>
      ) : phase === 'failed' ? (
        <div className="roster-notice">
          <p className="hint error">{t('loadFailed')}</p>
          <SecondaryButton onClick={retryLoad}>{tCommon('retry')}</SecondaryButton>
        </div>
      ) : (
        <>
          {members.length === 0 ? (
            <Row left={t('empty')} />
          ) : (
            members.map((row) => (
              <MemberRow
                key={rowKey(row)}
                row={row}
                t={t}
                canManage={canManage}
                onOpen={() => openSheet(row)}
              />
            ))
          )}
        </>
      )}
      {/*
        **Pinned, and both rows in one footer.** Three controls below a roster of forty are three
        controls nobody scrolls to. `.screen-footer` is the app bar's own sticky rule at the other
        end of the same scroller; a resident renders none of this, so their list runs to the bottom
        of the screen as it always did.

        The inner `.form-row`s are unchanged (fix A): `.btn-primary` alone is edge-to-edge and
        bleeds past the inset every other bottom-of-screen control keeps.

        P15 surface 4 (D115/D118) — the Access list's two copy-members entry points. `direction`
        follows the router's own semantics (`web/src/router/routes.ts`): "from" means the anchor
        gate is the *target* and the manager picks a source; "to" means the anchor is the
        *source* and the manager picks a target. P14 refuses a non-manager of either gate
        server-side — surfacing this only to a manager of the anchor gate is UX, not the control.
      */}
      {canManage ? (
        <div className="screen-footer">
          <div className="form-row">
            <button
              className="btn-primary"
              type="button"
              onClick={() => navigate.navigate({ id: 'invite', gateId })}
            >
              {t('addMember')}
            </button>
          </div>
          <div className="form-row">
            <button
              className="btn-secondary btn-with-icon"
              type="button"
              onClick={() => navigate.navigate({ id: 'copyMembers', gateId, direction: 'from' })}
            >
              <Icon name="import" size={15} /> {t('copyFrom')}
            </button>
            <button
              className="btn-secondary btn-with-icon"
              type="button"
              onClick={() => navigate.navigate({ id: 'copyMembers', gateId, direction: 'to' })}
            >
              <Icon name="send" size={15} /> {t('copyTo')}
            </button>
          </div>
        </div>
      ) : null}

      {selectedRow !== undefined ? (
        <FrameOverlay>
          <div className="member-sheet-backdrop" onClick={closeSheet}>
            <div
              className="member-sheet"
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="member-sheet-identity">
                <div className="member-sheet-name">{selectedRow.displayName}</div>
                {selectedRow.phone !== undefined ? (
                  <div className="member-sheet-phone">
                    <Bidi>{selectedRow.phone}</Bidi>
                  </div>
                ) : null}
              </div>

              {orphansTheGate(selectedRow) ? (
                // Checked before `expired`, and the order is deliberate: an expired grant is
                // refused by the server anyway, whereas this one the server would happily accept.
                // The more consequential explanation is the one to show.
                <p className="hint">{t('soleManagerRoleHint')}</p>
              ) : selectedRow.status === 'expired' ? (
                // §7.7's re-send (not built here) is the only way back for an expired grant — the
                // server answers `409 grant-not-editable` on a role `PATCH` to one by design
                // (`members/routes.ts`), so the toggle is withheld rather than offered and refused.
                <p className="hint">{t('expiredRoleHint')}</p>
              ) : (
                <div className="filter-row" role="group" aria-label={t('roleToggleLabel')}>
                  <button
                    className={`filter-chip${selectedRow.role === 'resident' ? ' on' : ''}`}
                    type="button"
                    disabled={busy}
                    aria-pressed={selectedRow.role === 'resident'}
                    onClick={() => void changeRole(selectedRow, 'resident')}
                  >
                    {t('roleResident')}
                  </button>
                  <button
                    className={`filter-chip${selectedRow.role === 'manager' ? ' on' : ''}`}
                    type="button"
                    disabled={busy}
                    aria-pressed={selectedRow.role === 'manager'}
                    onClick={() => void changeRole(selectedRow, 'manager')}
                  >
                    {t('roleManager')}
                  </button>
                </div>
              )}

              {/*
                **D269 — the same three states the invite row offers, on the grant that already
                exists.** Withheld for an `expired` grant on the role toggle's own rule: the server
                answers `409 grant-not-editable` to any `PATCH` on one, and an expired row is
                revoke-only until §7.7's re-send is built. Offered for the sole manager, where the
                role toggle is not: D143's guard is about `managerCount`, and a restriction reaches
                only `evaluateAccess()`'s command path, so it can never cost anyone the ability to
                undo it.

                **The chips draw glyphs and print no labels (device pass, amending D269's first
                build).** Both transports is the `bluetooth` + `wifi` pair, cloud-only `wifi`,
                BLE-only `bluetooth` — `TRANSPORT_ICONS`, the vocabulary the invite row drew before
                the owner dropped that control, so the glyphs mean the same thing wherever they
                appear. The words stay as each chip's `aria-label`, so a screen reader hears
                exactly what the labels used to say. **Colour is never the only carrier of the
                state** — the glyph says which and `aria-pressed` says that it is chosen — which is
                what lets the fill be the thing the eye reads first.
              */}
              {selectedRow.status === 'expired' ? null : (
                <>
                  <div className="member-sheet-field-label">{tInvite('transportLabel')}</div>
                  <div className="filter-row" role="group" aria-label={tInvite('transportLabel')}>
                    {TRANSPORT_ORDER.map((choice) => {
                      const on = transportChoiceOf(selectedRow.transportRestriction) === choice
                      return (
                        <button
                          key={choice}
                          className={`filter-chip transport-choice is-${choice}${on ? ' on' : ''}`}
                          type="button"
                          disabled={busy}
                          aria-pressed={on}
                          aria-label={tInvite(TRANSPORT_KEYS[choice])}
                          onClick={() => void changeTransport(selectedRow, choice)}
                        >
                          {TRANSPORT_ICONS[choice].map((name) => (
                            <Icon key={name} name={name} />
                          ))}
                        </button>
                      )
                    })}
                  </div>
                </>
              )}

              {!isSelf(selectedRow) ? (
                confirmingRevoke ? (
                  <div className="member-sheet-confirm">
                    <p className="hint">{t('revokeConfirmBody')}</p>
                    <div className="member-sheet-confirm-actions">
                      <SecondaryButton disabled={busy} onClick={() => setConfirmingRevoke(false)}>
                        {tCommon('cancel')}
                      </SecondaryButton>
                      <PrimaryButton
                        className="member-sheet-revoke-confirm"
                        disabled={busy}
                        onClick={() => void revoke(selectedRow)}
                      >
                        {t('revokeConfirm')}
                      </PrimaryButton>
                    </div>
                  </div>
                ) : (
                  <SecondaryButton
                    className="member-sheet-revoke"
                    disabled={busy}
                    onClick={() => setConfirmingRevoke(true)}
                  >
                    {t('revoke')}
                  </SecondaryButton>
                )
              ) : null}

              {mutationError !== undefined ? (
                <p className="hint error">
                  {mutationError === 'grant-not-editable' ? t('grantNotEditable') : t('mutationFailed')}
                </p>
              ) : null}
            </div>
          </div>
        </FrameOverlay>
      ) : null}

      {/*
        Outside the member sheet's conditional, and inside its own `FrameOverlay`. The sheet is
        dismissed by the handlers that raise this (`loadRoster()` may drop the selected row), so
        nesting it there would let the alert vanish with the thing it is explaining.
      */}
      {lastManagerAlert ? (
        <FrameOverlay>
          <AlertPopover
            title={t('lastManagerTitle')}
            body={t('lastManagerBody')}
            dismissLabel={tCommon('cancel')}
            onDismiss={() => setLastManagerAlert(false)}
          />
        </FrameOverlay>
      ) : null}
    </>
  )
}

function rowKey(row: RosterRow): string {
  return row.userId ?? row.phone ?? row.createdAt
}

function MemberRow({
  row,
  t,
  canManage,
  onOpen,
}: {
  readonly row: RosterRow
  readonly t: (key: Key<'people'>, vars?: Readonly<Record<string, string | number>>) => string
  readonly canManage: boolean
  onOpen(): void
}): JSX.Element {
  const content = (
    <>
      <div className="l">
        {/* **The role, now that no heading carries it.** An icon has no text for a screen reader,
            so `roleManager` is its accessible name — the key the member sheet's own toggle already
            uses, rather than a second string saying the same word. It leads the row: what a glyph
            after a name of unknown length marks is whichever name happens to be short. */}
        {/* `<Bidi>` on the name, not only on the phone below it (D178). This was the one member
            name in the app rendered unisolated: the shell is `dir="rtl"`, so an LTR self-name
            truncates from its *head* here — the same class of defect as the app-bar title's, which
            a complete iOS pass missed because the seed data was Hebrew-only. */}
        <Bidi>{row.displayName}</Bidi>{' '}
        {row.role === 'manager' ? (
          <>
            {/* **The glyph trails the name, in the same run as the two chips.** It is a mark *on*
                the person rather than a bullet the list is indented from, which is what a leading
                one reads as — and the row already has a vocabulary for that: `installer` and
                `expired` are chips in exactly this position. `Icon` renders `aria-hidden` on its
                own `<svg>` — deliberately, since almost every glyph in the app labels a control
                that has its own name — so the wrapper is what carries one here. */}
            <span role="img" aria-label={t('roleManager')}>
              <Icon name="user" size={14} />
            </span>{' '}
          </>
        ) : null}
        {row.installer ? <span className="chip">{t('installerChip')}</span> : null}
        {row.status === 'expired' ? <span className="chip chip-fail">{t('expiredChip')}</span> : null}
        <MemberPhone
          {...(row.phone === undefined ? {} : { phone: row.phone })}
          displayName={row.displayName}
        />
      </div>
    </>
  )
  if (!canManage) return <div className="row">{content}</div>
  return (
    <button className="row" data-interactive="true" type="button" onClick={onOpen}>
      {content}
    </button>
  )
}

function Row({ left }: { readonly left: string }): JSX.Element {
  return (
    <div className="row">
      <div className="l">{left}</div>
    </div>
  )
}
